import {MedicationProfileCard} from '@/modules/drug-catalog/medication-profiles/components'
import {TradeNameColumnsDef} from '@/modules/products/trade-names/components'
import {openTradeNameDrawer} from '@/modules/products/trade-names/components/trade-name-drawer/util'
import {DataList} from '@cypro/ui/components/ui/data-list'
import {DataSection} from '@cypro/ui/components/ui/data-section'
import {DataTable} from '@cypro/ui/components/ui/data-table'
import {EmptyState} from '@cypro/ui/components/ui/empty-state'
import {DrugsCategoryTag} from '@/shared/components/ui/tags/drugs-category-tag'
import {UserTag} from '@/shared/components/ui/tags/user-tag'
import {useAppForm} from '@cypro/ui/integrations/form'
import {getDatetimeFormatter, isDefined} from '@cypro/ui/utils'
import {fuzzyFilter} from '@/shared/utils/table'
import {Badge, HStack} from '@chakra-ui/react'
import {DrugCatalogItem, TradeName} from '@cypro/engine/schema'
import {useStore} from '@tanstack/react-form'
import {getCoreRowModel, getFilteredRowModel, useReactTable} from '@tanstack/react-table'
import {useTranslate} from '@tolgee/react'
import {useMemo} from 'react'

interface Props {
  drugItem: DrugCatalogItem
}

export function DrugItemDrawerAboutSection({drugItem}: Props) {
  const {t} = useTranslate('common')

  const about = useMemo(() => {
    const externalInformationLink =
      drugItem?.activeSubstanceDetails?.externalInformationLink ||
      drugItem?.supportiveMedicationDetails?.externalInformationLink

    return [
      {label: t('substance_id'), value: drugItem?.id},
      {label: t('name'), value: drugItem?.name},
      {label: t('prescription_tradename'), value: drugItem?.prescribingName},
      {label: t('internal_id'), value: drugItem?.code},
      {label: t('national_id'), value: drugItem?.nationalDrugId},
      {label: t('atc_code'), value: drugItem?.atcCode},
      {label: t('description'), value: drugItem?.description},
      ...(isDefined(externalInformationLink)
        ? [{label: t('external_information_link'), value: externalInformationLink}]
        : []),
      {
        label: t('created_by'),
        value: drugItem?.created ? <UserTag user={drugItem?.created} /> : '',
      },
      {
        label: t('date'),
        value: drugItem?.createdAt ? getDatetimeFormatter().format(drugItem?.createdAt) : '',
      },
    ]
  }, [t, drugItem])

  return (
    <DataSection label={t('about')}>
      <DataList items={about} />
    </DataSection>
  )
}

export function DrugItemDrawerClassificationSection({drugItem}: Props) {
  const {t} = useTranslate('common')

  const classification = useMemo(() => {
    const categories = drugItem?.categories?.map(item => item.drugCategory) || []

    return [
      {
        label: t('categories'),
        value: categories.length ? (
          <HStack flexWrap="wrap">
            {categories.map(categ => {
              if (!categ || categ.parentId) return null
              return (
                <Badge key={categ.id}>
                  <DrugsCategoryTag category={categ} />
                </Badge>
              )
            })}
          </HStack>
        ) : null,
      },
      {
        label: t('subcategories'),
        value: categories.length ? (
          <HStack flexWrap="wrap">
            {categories.map(categ => {
              if (!categ || !categ.parentId) return null
              return (
                <Badge key={categ.id}>
                  <DrugsCategoryTag category={categ} />
                </Badge>
              )
            })}
          </HStack>
        ) : null,
      },
    ]
  }, [t, drugItem])

  return (
    <DataSection label={t('classification')}>
      <DataList items={classification} />
    </DataSection>
  )
}

export function DrugItemDrawerMedicationProfilesSection({drugItem}: Props) {
  const {t} = useTranslate('common')

  return (
    <DataSection>
      {!drugItem?.medicationProfiles?.length && <EmptyState title={t('no_medicine_forms_added_yet')} />}

      {drugItem?.medicationProfiles?.map(profile => (
        <MedicationProfileCard key={profile.id} profile={profile} />
      ))}
    </DataSection>
  )
}

interface DrugItemDrawerTradeNamesSectionProps {
  tradeNames: TradeName[] | undefined
}

export function DrugItemDrawerTradeNamesSection({tradeNames}: DrugItemDrawerTradeNamesSectionProps) {
  const {t} = useTranslate('common')

  const form = useAppForm({
    defaultValues: {
      search: '',
    },
  })
  const search = useStore(form.store, state => state.values.search)

  const columns = useMemo(() => {
    return [
      TradeNameColumnsDef.name({maxSize: 28}),
      TradeNameColumnsDef.form({maxSize: 28}),
      TradeNameColumnsDef.strength({maxSize: 32}),
      TradeNameColumnsDef.stock({maxSize: 12}),
    ]
  }, [])

  const table = useReactTable({
    data: tradeNames ?? [],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter(),
    },
    globalFilterFn: 'fuzzy',
    state: {
      globalFilter: search,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  return (
    <DataSection spaceY="8" display="flex" flexDirection="column">
      {!tradeNames?.length && <EmptyState title={t('no_trade_names_added_yet')} />}

      {Boolean(tradeNames?.length) && (
        <>
          <form onSubmit={event => event.preventDefault()}>
            <form.AppForm>
              <form.AppField name="search">{field => <field.Searchbox maxWidth="100%" />}</form.AppField>
            </form.AppForm>
          </form>

          <DataTable table={table} onRowClick={openTradeNameDrawer} />
        </>
      )}
    </DataSection>
  )
}
